import { Fragment, useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { FadeUp, MaskedLines } from "@/components/Reveal";

const chapters = [
  {
    n: "01",
    title: "Site Inspection",
    copy: "Every fantasy begins with a tape measure and a sketch. We walkthrough your venue, map every corner, and template the floor down to the inch—because when every detail matters, precision keeps the magic intact.",
  },
  {
    n: "02",
    title: "Stirring the Pot of Magic",
    copy: "No two dreams are alike — so no two floors are either. Every design is custom-made around your vision and your event: our design team takes your colors, your monogram, your wildest idea, and stirs them into something truly magical, refined until it is unmistakably yours.",
  },
  {
    n: "03",
    title: "The Fairy Dust is Sprinkled",
    copy: "Installation day. Our specialists lay out and install your dream floor panel by panel, seam by seam — measured twice, smoothed by hand and polished until the room is ready for its transformation.",
  },
  {
    n: "04",
    title: "The GRAND Reveal",
    copy: "The doors open, your guests step into the room — and stop. There, at the center of everything, sits a beautiful masterpiece: your floor, your story, the centerpiece no one will stop talking about.",
  },
  {
    n: "05",
    title: "Your Happily Ever After Begins",
    copy: "The first dance, the photos, the memories that outlive the night. Long after the last song fades, your floor is what everyone remembers — the moment the fantasy became real.",
  },
];

export default function Manifesto() {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const step = chapters[active];

  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => setActive((a) => (a + 1) % chapters.length), 4500);
    return () => clearInterval(id);
  }, [paused]);

  return (
    <section id="craft" data-testid="manifesto-section" className="relative py-32 sm:py-44">
      <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
        <FadeUp className="mb-6">
          <p className="text-[12px] tracking-[0.45em] uppercase text-coral">The Craft</p>
        </FadeUp>
        <h2 className="font-serif font-light text-ink leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl max-w-3xl mb-12 sm:mb-16">
          <MaskedLines
            inView
            lines={[
              <>Magic is not found.</>,
              <><span className="italic text-coral">It is manufactured</span> — by hand.</>,
            ]}
          />
        </h2>

        <FadeUp className="mb-12 sm:mb-16" delay={0.1}>
          <div data-testid="craft-steps-strip" className="flex flex-wrap items-center gap-x-3 gap-y-4">
            {chapters.map((c, i) => (
              <Fragment key={c.n}>
                <button
                  data-testid={`craft-step-btn-${i}`}
                  onClick={() => {
                    setPaused(true);
                    setActive(i);
                  }}
                  className={`px-5 py-2.5 rounded-full text-[11px] tracking-[0.18em] uppercase whitespace-nowrap transition-colors duration-300 ${
                    active === i
                      ? "bg-ink text-ivory"
                      : "border border-ink/15 bg-white/50 text-ink/70 hover:border-coral hover:text-coral"
                  }`}
                >
                  {c.title}
                </button>
                {i < chapters.length - 1 && <ArrowRight size={15} className="text-coral shrink-0" />}
              </Fragment>
            ))}
          </div>
        </FadeUp>

        <div className="min-h-[280px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step.n}
              data-testid="craft-step-detail"
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -16 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="grid lg:grid-cols-12 gap-10 items-center"
            >
              <div className="lg:col-span-11">
                <h3 className="font-serif italic text-3xl sm:text-4xl text-ink mb-6">{step.title}</h3>
                <p className="text-base sm:text-lg font-light text-ink/65 leading-relaxed max-w-xl">{step.copy}</p>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-24 sm:mt-32 grid lg:grid-cols-12 gap-12 items-start">
          <FadeUp className="lg:col-span-5">
            <h3 className="font-serif font-light text-ink text-3xl sm:text-4xl leading-tight mb-8">
              Professional <span className="italic text-coral">Installations</span>, From Start to Finish
            </h3>
            <p className="text-base font-light text-ink/65 leading-relaxed">
              Installing dancefloors, wraps and logos is strenuous, meticulous work.
              From the first team meeting about your logistics to the final polish,
              every detail is handled so the design aligns perfectly with your vision.
            </p>
          </FadeUp>
          <FadeUp delay={0.15} className="lg:col-span-4">
            <ul className="space-y-5">
              {[
                "Certified & professional dancefloor specialists",
                "Precision installations — 2 to 12+ hours",
                "Professional setup & breakdown crews",
                "Delivery & pickup",
                "Dancefloor cleanup & trash disposal",
              ].map((duty) => (
                <li key={duty} className="flex items-start gap-4 text-base font-light text-ink/75">
                  <span className="text-gold mt-1 text-xs">✦</span>
                  {duty}
                </li>
              ))}
            </ul>
          </FadeUp>
          <FadeUp delay={0.3} className="lg:col-span-3">
            <div data-testid="subfloor-note" className="bg-white/70 backdrop-blur-xl border border-gold/30 rounded-[2rem] p-8 spotlight-frame">
              <p className="text-[11px] tracking-[0.35em] uppercase text-gold mb-4">The Subfloor Promise</p>
              <p className="text-sm font-light text-ink/70 leading-relaxed mb-4">
                Even the most prestigious venues in New England trust us with their floors.
                Before a single panel is laid, we verify your venue's floor won't be damaged —
                and if it needs protection, we lay a subfloor between their floor and your wrap.
              </p>
              <p className="text-xs text-ink/45 font-light leading-relaxed">
                Site-visit inspections carry a non-refundable $100 fee + travel.
              </p>
            </div>
          </FadeUp>
        </div>
      </div>
    </section>
  );
}
